import { PromotionStateEnum } from '@koob/enums';
import { Stack } from '@koob/margaret';
import { getPromotions, getPromotionStateCounts } from 'api/node/promotions';
import { PER_PAGE_FOR_CATALOG } from 'constants';

import KoobTitle from 'components/Koob/KoobTitle';
import PromotionsListTable from 'components/Promotions/PromotionsListTable';
import { KoobCreateButton, KoobCard, KoobInputSegmented } from '@koob/ui';
import { SectionWrapper } from 'containers/Hotels/Hotel/Promotions/PromotionsList';
import { useSearchParams } from 'hooks';
import { isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { constantCase, ProductTypeEnum } from 'utils';

const PromotionsList = ({ isReadOnly = false }) => {
  const theme = useTheme();
  const { experienceId } = useParams();
  const [searchParams] = useSearchParams();
  const [promotionState, setPromotionState] = useState(
    PromotionStateEnum.OPERATIONAL,
  );
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE_FOR_CATALOG);

  const sortParams = { ...searchParams };

  const { data, isLoading } = useAsync({
    promiseFn: getPromotions,
    productId: experienceId,
    kind: ProductTypeEnum.EXPERIENCE,
    state: promotionState,
    column: sortParams?.order,
    direction: sortParams?.direction && constantCase(sortParams.direction),
    page,
    perPage,
    watchFn: (props, prevProps) => {
      return (
        !isEqual(props.column, prevProps.column) ||
        !isEqual(props.direction, prevProps.direction) ||
        !isEqual(props.state, prevProps.state) ||
        !isEqual(props.page, prevProps.page) ||
        !isEqual(props.perPage, prevProps.perPage)
      );
    },
  });

  const { data: byStateTotal } = useAsync({
    promiseFn: getPromotionStateCounts,
    productId: experienceId,
    kind: ProductTypeEnum.EXPERIENCE,
  });

  useEffect(() => {
    setPage(1);
  }, [promotionState, sortParams.order, sortParams.direction]);

  const { t } = useTranslation(['experiences', 'promotions', 'misc']);
  const headers = [
    {
      key: 'name',
      label: t('promotionName', { ns: 'promotions' }),
      width: '20%',
      sortable: true,
    },
    {
      key: 'benefit',
      label: t('benefits', { ns: 'promotions' }),
      width: '12%',
    },
    {
      key: 'status',
      label: t('status', { ns: 'promotions' }),
      width: '12%',
      sortable: true,
    },
    {
      key: 'appliesTo',
      label: t('appliesTo.label', { ns: 'promotions' }),
      width: '12%',
    },
    {
      key: 'tos',
      label: t('tos', { ns: 'promotions' }),
      width: '8%',
    },
    {
      key: 'action',
      label: '',
      width: '5%',
    },
  ];

  const handleStateSelection = (value) => {
    setPromotionState(() => value);
  };

  const stateOptions = [
    {
      value: PromotionStateEnum.OPERATIONAL,
      label: `${t('operationalStatus', { ns: 'promotions' })} (${
        byStateTotal?.operational ?? 0
      })`,
    },
    {
      value: PromotionStateEnum.DRAFT,
      label: `${t('draftStatus', { ns: 'promotions' })} (${
        byStateTotal?.draft ?? 0
      })`,
    },
    {
      value: PromotionStateEnum.ARCHIVED,
      label: `${t('archivedStatus', { ns: 'promotions' })} (${
        byStateTotal?.archived ?? 0
      })`,
    },
  ];

  return (
    <div>
      <KoobCard>
        <div className="flex justify-center">
          <div className="text-center">
            <KoobTitle>{t('promotionTitle')}</KoobTitle>
            <div className="text-lg font-medium text-gray-600">
              {t('promotionDetails')}
            </div>
          </div>
        </div>
        <SectionWrapper>
          <Stack
            direction="row"
            alignY="center"
            style={{
              marginTop: theme.spacing(1),
              marginBottom: theme.spacing(0.25),
            }}
          >
            <KoobInputSegmented
              options={stateOptions}
              value={promotionState}
              onChange={handleStateSelection}
            />
            {!isReadOnly && (
              <div style={{ marginLeft: 'auto' }}>
                <KoobCreateButton to="./new">
                  {t('new', { ns: 'promotions' })}
                </KoobCreateButton>
              </div>
            )}
          </Stack>
          <PromotionsListTable
            isExperience
            data={data?.data}
            loading={isLoading}
            headers={headers}
            isReadOnly={isReadOnly}
            pagination={{
              page,
              setPage,
              perPage,
              setPerPage,
              totalCount: Number(data?.headers?.xTotalCount ?? 0),
              totalPages: Number(data?.headers?.xTotalPages ?? 0),
            }}
          />
        </SectionWrapper>
      </KoobCard>
    </div>
  );
};

export default PromotionsList;
